import React, {
  memo,
  type ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useDispatch } from 'react-redux';
import {
  ApplicationIcon,
  Box,
  Button,
  Icon,
  ListRow,
  ListRowGroup,
  TabContent,
  Typography,
} from '@saviynt/design-system';
import cx from 'classnames';

import { SodMessage, SodReloader } from '../../../../../shared/components';
import {
  ALLOWED_ACTIONS,
  LIST_ACTIONS,
  VIOLATIONS_DETAILS_TABS,
} from '../../../../../shared/constants';
import {
  useInvertedLocalize,
  useTypedSelector,
} from '../../../../../shared/hooks';
import { createDevRejectionHandler } from '../../../../../shared/utils';
import { type ViolationMitigatingControls } from '../../../data-layer/detailsTypes';
import { detailsActions } from '../../../data-store/detailsSlice';
import { useHandleMitigatingControl } from '../../../hooks';

import ViolationsTabDetailsMitigatingLoader from './ViolationsTabDetailsMitigatingLoader';
import {
  MitigateCenterContent,
  MitigateTralingContent,
} from './ViolationsTabDetailsMitigatingShared';

import './ViolationsTabDetailsMitigating.css';

interface ViolationsTabDetailsMitigatingProps {
  sodKey: string;
  activeTab: string;
  className?: string;
}

/**
 * ViolationsTabDetailsMitigating
 *
 * TODO:
 * - Localize Strings
 * - Intigration testing
 */
function ViolationsTabDetailsMitigating({
  sodKey,
  activeTab,
  className,
}: ViolationsTabDetailsMitigatingProps): ReactElement {
  const dispatch = useDispatch();
  const l = useInvertedLocalize();

  const {
    sidepanel: { isSidePanelVisible },
    violationsTab: {
      mitigate: { selectedRowData, isListRefresh },
      actionManager,
    },
  } = useTypedSelector((state) => state.aag.details);

  const [isRefreshMcList, setIsRefreshMcList] = useState(true);
  const [listRows, setListRows] = useState([] as ViolationMitigatingControls[]);

  const { loadMitigateRow, mitigatingControlRows, isError, isLoading } =
    useHandleMitigatingControl({ isCacheDisabled: true });

  const {
    aag: {
      global: { globalDateFormat },
      violationsTab: {
        controls: { tabInAction },
      },
    },
  } = useTypedSelector((state) => state);

  // Constants
  const actionsList = useMemo(() => {
    if (!actionManager) {
      return [];
    }

    const { allowedActions } = actionManager;

    if (!allowedActions) {
      return [];
    }

    const tempList: string[] = [];

    allowedActions.forEach((action) => {
      if (action in ALLOWED_ACTIONS) {
        tempList.push(
          ...ALLOWED_ACTIONS[action as keyof typeof ALLOWED_ACTIONS]
        );
      }
    });

    return tempList;
  }, [actionManager]);
  const getOptionDisabledStatus = useCallback(
    (value: string) => {
      if (actionManager?.isReadOnlyActions) {
        return !(
          value === LIST_ACTIONS.VIEWDETAILS.value ||
          value === LIST_ACTIONS.GENERATEREPORT.value
        );
      }

      return !actionsList.includes(value);
    },
    [actionManager?.isReadOnlyActions, actionsList]
  );

  const refreshList = useCallback(() => {
    setIsRefreshMcList(true);
  }, []);

  useEffect(() => {
    // Only call API when this tab is active
    if (
      activeTab === VIOLATIONS_DETAILS_TABS.TAB_MITIGATING &&
      sodKey &&
      isRefreshMcList
    ) {
      const requestData = {
        id: parseInt(sodKey, 10),
        violationId: sodKey,
      };

      loadMitigateRow(requestData)
        .catch(
          createDevRejectionHandler(
            'ViolationsTabDetailsMitigating: loadMitigateRow'
          )
        )
        .finally(() => setIsRefreshMcList(false));
    }

    return undefined;
  }, [activeTab, loadMitigateRow, sodKey, isRefreshMcList]);

  useEffect(() => {
    setListRows(mitigatingControlRows);
  }, [mitigatingControlRows]);

  useEffect(() => {
    if (isListRefresh) {
      refreshList();

      dispatch(detailsActions.setViolationTabDetailsMcListRefresh(false));
    }
  }, [dispatch, isListRefresh, refreshList]);

  const handleRowClick = useCallback(
    (row: ViolationMitigatingControls) => {
      dispatch(detailsActions.setSelectedMitigateRow(row));
      dispatch(detailsActions.setIsSidePanelVisible(true));
    },
    [dispatch]
  );

  const mitigatingtabContentClasses = cx(
    'Sod-ViolationsTabDetailsMitigating',
    className
  );

  if (isLoading) {
    return <ViolationsTabDetailsMitigatingLoader />;
  }

  if (isError) {
    return (
      <SodReloader
        triggerRefresh={() => {
          if (sodKey) {
            const requestData = {
              id: parseInt(sodKey, 10),
              violationId: sodKey,
            };

            // Force a refresh by deleting the cache entry
            loadMitigateRow(requestData).catch(
              createDevRejectionHandler(
                'ViolationsTabDetailsMitigating: loadMitigateRow refresh'
              )
            );
          }
        }}
      />
    );
  }

  if (listRows.length === 0) {
    return (
      <SodMessage
        isSearchActive={false}
        isFilterActive={false}
        handleFilterClearBtn={() => {}}
      />
    );
  }

  const scrollWrapperClasses = cx(
    'Sod-ViolationsTabDetailsMitigating-nestedListGroupScrollArea',
    isSidePanelVisible &&
      'Sod-ViolationsTabDetailsMitigating-nestedListGroupScrollArea--isSidePanelVisible'
  );

  return (
    <TabContent
      value={activeTab}
      tabValue={VIOLATIONS_DETAILS_TABS.TAB_MITIGATING}
      className={mitigatingtabContentClasses}>
      <Box className='Sod-ViolationsTabDetailsMitigating-listHeading'>
        <Typography kind='h3'>
          {l('Applied Mitigating Control ({count})', {
            count: listRows.length,
          })}
        </Typography>
        {tabInAction !== null && !isSidePanelVisible && (
          <Button
            type='button'
            kind='outlined'
            onClick={() => {
              dispatch(
                detailsActions.setViolationTabdetailsActionManager({
                  ...actionManager,
                  isOpen: true,
                  sodKey: actionManager?.sodKey ?? '',
                  action: LIST_ACTIONS.ADDMC.value,
                })
              );
            }}
            isDisabled={getOptionDisabledStatus(LIST_ACTIONS.ADDMC.value)}
            leftIcon={<Icon kind='Add' />}>
            {l('Add')}
          </Button>
        )}
      </Box>
      <Box tag='div' className={scrollWrapperClasses}>
        <ListRowGroup className='Sod-ViolationsTabDetailsMitigating-list'>
          {listRows.map((row) => (
            <ListRow
              key={row.id}
              centerContent={
                <MitigateCenterContent
                  row={row}
                  globalDateFormat={globalDateFormat}
                />
              }
              className='Sod-ViolationsTabDetailsMitigating-listItem'
              leadingComp={
                <ApplicationIcon icon='MitigatingControls' size='medium' />
              }
              trailingContent={
                tabInAction !== null && !isSidePanelVisible ? (
                  <MitigateTralingContent
                    mcKey={Number(row.id)}
                    isDisabled={Boolean(actionManager?.isReadOnlyActions)}
                    actionsList={actionsList}
                  />
                ) : undefined
              }
              handleRowClick={() => handleRowClick(row)}
              isRowDetailsOpen={selectedRowData?.id === row.id}
              connectorDistance='1.625rem'
              isRowConnectTransitionEnabled
              isCheckboxDisabled
            />
          ))}
        </ListRowGroup>
      </Box>
    </TabContent>
  );
}

ViolationsTabDetailsMitigating.defaultProps = {
  className: null,
};

export default memo(ViolationsTabDetailsMitigating);
